// İSG uzmanı portalı — Risk Değerlendirme listesi (api-contract §19.1, tenant-scoped).
// `GET /risk-assessments` (sayfalı + filtreli + arama). Tablo: başlık, saha, değerlendirme
// tarihi, **durum rozeti**, revizyon no, kalem sayısı, **en yüksek seviye** (renkli rozet).
// Filtreler: durum, saha (Select), **seviye**, tarih aralığı (from/to), arama. "Yeni
// Değerlendirme" birincil butonu → editör; her satırda Detay (→ /risk-assessments/{uuid}).
// Sıra backend'de `assessmentDate` azalan (istemci sort göndermez).

import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  type RiskAssessmentStatus,
  type RiskLevel,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLink,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  currentCulture,
  resolveApiErrorMessage,
  riskApi,
  settingsApi,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { Plus, SearchX, ShieldAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MoreFiltersToggle } from '../../components/MoreFiltersToggle';
import { RiskLevelBadge } from '../../components/risk/RiskLevelBadge';
import { RISK_LEVELS, RISK_STATUSES, riskStatusTone } from '../../components/risk/riskEnums';
import { formatDate } from '../../lib/format';
import { riskKeys } from '../../lib/riskKeys';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;
/** Saha seçicisini tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const OPTIONS_PAGE_SIZE = 200;

/** Yerel gün başlangıcını UTC ISO'ya çevirir (from filtresi — §4 wire). */
function dayStartIso(dateStr: string): string | undefined {
  if (!dateStr) {
    return undefined;
  }
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** Yerel gün sonunu UTC ISO'ya çevirir (to filtresi — §4 wire). */
function dayEndIso(dateStr: string): string | undefined {
  if (!dateStr) {
    return undefined;
  }
  const d = new Date(`${dateStr}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function RiskAssessmentsListPage() {
  const { t } = useTranslation();
  const culture = currentCulture();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [status, setStatus] = useState('');
  const [siteUuid, setSiteUuid] = useState('');
  const [level, setLevel] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  // Owner review B2 item 2 — aynı desen `PermitsListPage.tsx`'ten (bkz. o dosyanın notu): iki
  // tarih filtresi (`from`/`to`) "Daha fazla filtre" düğmesinin ARKASINA katlanır; bu sayfada
  // bir drill-through URL'i (`from`/`to`'yu doldurabilecek) YOK, bu yüzden başlangıç hep kapalı.
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);

  // Saha filtresi (§10). Bu uç kendi grant'ını gerektirir; yetki yoksa liste boş döner
  // (yalnız "tümü" seçeneği görünür) — filtre bozulmaz.
  const sitesQuery = useQuery({
    queryKey: ['risk', 'options', 'sites'],
    queryFn: () =>
      settingsApi.listSites({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    staleTime: 60_000,
  });

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('risk.list.filter.allStatuses') },
      ...RISK_STATUSES.map((s) => ({ value: s, label: t(`risk.status.${s}`) })),
    ],
    [t],
  );
  const levelOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('risk.list.filter.allLevels') },
      ...RISK_LEVELS.map((l) => ({ value: l, label: t(`risk.level.${l}`) })),
    ],
    [t],
  );
  const siteOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('risk.list.filter.allSites') },
      ...(sitesQuery.data?.items ?? []).map((s) => ({
        value: s.uuid,
        label: s.branchName ? `${s.name} · ${s.branchName}` : s.name,
      })),
    ],
    [t, sitesQuery.data],
  );

  const params = {
    status: (status || undefined) as RiskAssessmentStatus | undefined,
    siteUuid: siteUuid || undefined,
    level: (level || undefined) as RiskLevel | undefined,
    from: dayStartIso(from),
    to: dayEndIso(to),
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: riskKeys.listParams(params),
    queryFn: () => riskApi.listRiskAssessments(params),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch || status || siteUuid || level || from || to);
  // Owner review B2 item 2 — "Daha fazla filtre" rozeti; katlı 2 tarih filtresinden kaçı DOLU.
  const moreFiltersActiveCount = [from, to].filter(Boolean).length;
  function clearFilters() {
    setSearch('');
    setStatus('');
    setSiteUuid('');
    setLevel('');
    setFrom('');
    setTo('');
    setPage(1);
  }

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('risk.title') }]} />
      <PageHeader
        title={t('risk.title')}
        helperText={t('risk.list.subtitle')}
        actions={
          <Button onClick={() => navigate('/risk-assessments/new')}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('risk.list.new')}
          </Button>
        }
      />

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data ? t('risk.list.count', { count: query.data.filteredTotal }) : undefined
            }
            search={search}
            onSearchChange={resetPage(setSearch)}
            searchLabel={t('risk.list.search')}
            searchPlaceholder={t('risk.list.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <>
                {/* HER filtre `TableToolbarFilter`e AYRI AYRI sarmalı (S33 Slice 4 QA fix,
                    3. tur) — bkz. TableToolbar.tsx dosya başlığı. Görünür etiket HER filtrede
                    (S33-8 review fix #7) — bkz. `PermitsListPage.tsx`'in aynı notu. */}
                <TableToolbarFilter>
                  <Field htmlFor="risk-filter-status" label={t('risk.list.filter.status')}>
                    <SelectMenu
                      id="risk-filter-status"
                      placeholder={t('risk.list.filter.allStatuses')}
                      options={statusOptions}
                      value={status}
                      onValueChange={resetPage(setStatus)}
                    />
                  </Field>
                </TableToolbarFilter>
                <TableToolbarFilter>
                  <Field htmlFor="risk-filter-site" label={t('risk.list.filter.site')}>
                    <SelectMenu
                      id="risk-filter-site"
                      placeholder={t('risk.list.filter.allSites')}
                      options={siteOptions}
                      value={siteUuid}
                      onValueChange={resetPage(setSiteUuid)}
                    />
                  </Field>
                </TableToolbarFilter>
                <TableToolbarFilter>
                  <Field htmlFor="risk-filter-level" label={t('risk.list.filter.level')}>
                    <SelectMenu
                      id="risk-filter-level"
                      placeholder={t('risk.list.filter.allLevels')}
                      options={levelOptions}
                      value={level}
                      onValueChange={resetPage(setLevel)}
                    />
                  </Field>
                </TableToolbarFilter>
                {/* Owner review B2 item 2 — `PermitsListPage.tsx`'in AYNI deseni: 2 tarih
                    filtresi bu düğmenin ARKASINA katlanır. */}
                <MoreFiltersToggle
                  open={moreFiltersOpen}
                  onToggle={() => setMoreFiltersOpen((v) => !v)}
                  activeCount={moreFiltersActiveCount}
                  label={t('common.moreFilters')}
                />
                {moreFiltersOpen ? (
                  <>
                    <TableToolbarFilter>
                      <Field htmlFor="risk-filter-from" label={t('risk.list.filter.from')}>
                        <Input
                          id="risk-filter-from"
                          type="date"
                          value={from}
                          onChange={(e) => resetPage(setFrom)(e.target.value)}
                        />
                      </Field>
                    </TableToolbarFilter>
                    <TableToolbarFilter>
                      <Field htmlFor="risk-filter-to" label={t('risk.list.filter.to')}>
                        <Input
                          id="risk-filter-to"
                          type="date"
                          value={to}
                          onChange={(e) => resetPage(setTo)(e.target.value)}
                        />
                      </Field>
                    </TableToolbarFilter>
                  </>
                ) : null}
              </>
            }
          />

          <Table caption={t('risk.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('risk.list.col.title')}</TableHead>
                <TableHead>{t('risk.list.col.site')}</TableHead>
                <TableHead>{t('risk.list.col.date')}</TableHead>
                <TableHead>{t('risk.list.col.status')}</TableHead>
                <TableHead className="text-right">{t('risk.list.col.revision')}</TableHead>
                <TableHead className="text-right">{t('risk.list.col.items')}</TableHead>
                <TableHead>{t('risk.list.col.highestLevel')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={7} />
              ) : query.isError ? (
                <TableMessageRow colSpan={7}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={7}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('risk.list.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={ShieldAlert}
                      title={t('risk.list.empty')}
                      description={t('risk.list.empty.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate('/risk-assessments/new')}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('risk.list.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((assessment) => (
                  <TableRow key={assessment.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = başlık hücresi (S33 Slice 4 QA fix, defekt #4) —
                          Document Types emsaliyle aynı, ayrı Actions sütunu yok. */}
                      <TableLink to={`/risk-assessments/${assessment.uuid}`} className="block">
                        {assessment.title}
                      </TableLink>
                      {assessment.area ? (
                        <span className="block text-xs text-muted">{assessment.area}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted">{assessment.siteName ?? '—'}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted tnum">
                      {formatDate(assessment.assessmentDate, culture)}
                    </TableCell>
                    <TableCell>
                      <Tag color={riskStatusTone(assessment.status)}>
                        {t(`risk.status.${assessment.status}`)}
                      </Tag>
                    </TableCell>
                    <TableCell className="text-right text-muted tnum">
                      {assessment.revisionNo}
                    </TableCell>
                    <TableCell className="text-right text-muted tnum">
                      {assessment.itemCount}
                    </TableCell>
                    <TableCell>
                      {assessment.highestLevel ? (
                        <RiskLevelBadge level={assessment.highestLevel} />
                      ) : (
                        <span className="text-xs text-muted">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('risk.pagination.nav'),
                previous: t('risk.pagination.previous'),
                next: t('risk.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
